import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './ChatbotBuilder.css';
import BasicSettings from './Customize/Profile/BasicSettings.js';
import StyleSettings from './Customize/Theme/StyleSettings.js';
import AdvanceSettings from './Customize/AdvanceSettings/AdvanceSettings.js';
import ProactiveBubbleSettings from './Customize/ProactiveBubble/ProactiveBubbleSettings.js';
import Integration from './Integrations/Integration/Integration.js';
import Publish from './Deploy/Publish/Publish.js';
import EmbedChatbot from '../../../Embed/EmbedChatbot';
import PropTypes from 'prop-types';
import ToggleButton from './Customize/ToggleButton/ToggleButton';
import { auth } from '../../../../firebase/config/config';
import logger from '../../../../utils/logger';
import Identity from './Customize/Design/Identity.js';
import ChatMenu from './Customize/ChatMenu/ChatMenu.js';
import Files from './KnowledgeBase/Files/Files.js';
import Forms from './Customize/Forms/Forms.js';
import HumanHandoff from './Customize/HumanHandoff/HumanHandoff.js';

function ChatbotBuilder({
  chatbot = {},
  onClose,
  isPublished,
  announcement,
  setAnnouncement,
  activeSubTab = 'Profile',
  setActiveSubTab,
}) {
  // Local state for the "Powered by Diraiah" toggle
  const [showPoweredBy, setShowPoweredBy] = useState(
    typeof chatbot.showPoweredBy !== 'undefined' ? chatbot.showPoweredBy : true
  );

  // Local state for the "Link Previews" toggle (rich preview cards for URLs in replies)
  const [showLinkPreviews, setShowLinkPreviews] = useState(
    typeof chatbot.showLinkPreviews !== 'undefined' ? chatbot.showLinkPreviews : true
  );

  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Local state for all configuration properties (each maintained separately)
  const [chatbotPosition, setChatbotPosition] = useState(chatbot.chatbotPosition || 'right'); // Default to right
  const [openAIAPIKey, setOpenAIAPIKey] = useState(chatbot.openAIAPIKey || '');
  const [assistantName, setAssistantName] = useState(chatbot.assistantName || '');
  const [messagePrompt, setMessagePrompt] = useState(chatbot.messagePrompt || '');
  const [introMessageText, setIntroMessageText] = useState(chatbot.introMessageText || '');
  const [customIntroMessage, setCustomIntroMessage] = useState(chatbot.customIntroMessage || false);
  const [defaultLanguage, setDefaultLanguage] = useState(chatbot.defaultLanguage || 'en');
  const [internalMessagePrompt, setInternalMessagePrompt] = useState(chatbot.internalMessagePrompt || '');

  const [botTagline, setBotTagline] = useState(chatbot.botTagline || '');
  const [humanHandoff, setHumanHandoff] = useState(chatbot.humanHandoff || null);
  const [officeHours, setOfficeHours] = useState(chatbot.officeHours || null);

  const [headerColor, setHeaderColor] = useState(chatbot.headerColor || '');
  const [chatBackgroundColor, setChatBackgroundColor] = useState(chatbot.chatBackgroundColor || '');
  const [forms, setForms] = useState([]);
  const [botMessageBoxColor, setBotMessageBoxColor] = useState(chatbot.botMessageBoxColor || '');
  const [botMessageTextColor, setBotMessageTextColor] = useState(chatbot.botMessageTextColor || '#333333');
  const [userMessageBoxColor, setUserMessageBoxColor] = useState(chatbot.userMessageBoxColor || '');
  const [userMessageTextColor, setUserMessageTextColor] = useState(chatbot.userMessageTextColor || '#ffffff');
  const [headerStyle, setHeaderStyle] = useState(chatbot.headerStyle || 'modern');
  const [botIconUrl, setBotIconUrl] = useState(chatbot.botIconUrl || '');
  const [botIconOffsetX, setBotIconOffsetX] = useState(chatbot.botIconOffsetX || 0);
  const [botIconOffsetY, setBotIconOffsetY] = useState(chatbot.botIconOffsetY || 0);
  // URL Exclusions
  const [excludedUrls, setExcludedUrls] = useState(chatbot.excludedUrls || []);

  // Advance Settings
  const [openAIVectorStoreID, setOpenAIVectorStoreID] = useState(chatbot.openAIVectorStoreID || '');
  const [openAIAssistantID, setOpenAIAssistantID] = useState(chatbot.openAIAssistantID || '');
  const [chatbotRolePrompt, setChatbotRolePrompt] = useState(chatbot.chatbotRolePrompt || '');
  const [aiModelTier, setAiModelTier] = useState(chatbot.aiModelTier || 'standard');
  const [customAPIEndpoint, setCustomAPIEndpoint] = useState('');
  const [responseDelay, setResponseDelay] = useState(0);
  const [openChatOnLoad, setOpenChatOnLoad] = useState(false);
  const [chatOpenDelay, setChatOpenDelay] = useState(0);
  const [soundNotificationEnabled, setSoundNotificationEnabled] = useState(true);

  // Integration Settings
  const [wooCommerceApiKey, setWooCommerceApiKey] = useState(chatbot.wooCommerceApiKey || '');
  const [wooCommerceApiSecret, setWooCommerceApiSecret] = useState(chatbot.wooCommerceApiSecret || '');
  const [wooCommerceStoreUrl, setWooCommerceStoreUrl] = useState(chatbot.wooCommerceStoreUrl || '');
  const [wooCommerceEnabled, setWooCommerceEnabled] = useState(chatbot.wooCommerceEnabled || false);

  // WhatsApp Integration Settings
  const [whatsAppEnabled, setWhatsAppEnabled] = useState(chatbot.whatsAppEnabled || false);
  const [whatsAppPhoneNumber, setWhatsAppPhoneNumber] = useState(chatbot.whatsAppPhoneNumber || '');
  const [whatsAppPhoneNumberId, setWhatsAppPhoneNumberId] = useState(chatbot.whatsAppPhoneNumberId || '');
  const [whatsAppBusinessAccountId, setWhatsAppBusinessAccountId] = useState(chatbot.whatsAppBusinessAccountId || '');
  const [whatsAppDisplayName, setWhatsAppDisplayName] = useState(chatbot.whatsAppDisplayName || '');
  const [whatsAppAccessToken, setWhatsAppAccessToken] = useState(chatbot.whatsAppAccessToken || '');



  // Toggle Button Settings
  const [toggleButtonSize, setToggleButtonSize] = useState(chatbot.toggleButtonSize || 'medium');
  const [toggleButtonIcon, setToggleButtonIcon] = useState(chatbot.toggleButtonIcon || '');
  const [customToggleButtonIcon, setCustomToggleButtonIcon] = useState(chatbot.customToggleButtonIcon || '');
  const [toggleButtonIconSize, setToggleButtonIconSize] = useState(chatbot.toggleButtonIconSize || '60');
  const [toggleButtonBackgroundColor, setToggleButtonBackgroundColor] = useState(chatbot.toggleButtonBackgroundColor || '#1a1a1a');
  const [toggleButtonIconOffsetX, setToggleButtonIconOffsetX] = useState(chatbot.toggleButtonIconOffsetX || 0);

  const [toggleButtonIconOffsetY, setToggleButtonIconOffsetY] = useState(chatbot.toggleButtonIconOffsetY || 0);
  const [toggleButtonOutlineEnabled, setToggleButtonOutlineEnabled] = useState(chatbot.toggleButtonOutlineEnabled || false);
  const [toggleButtonOutlineColor, setToggleButtonOutlineColor] = useState(chatbot.toggleButtonOutlineColor || '#ffffff');


  // Chat Menu Settings
  const [menuItems, setMenuItems] = useState(chatbot.menuItems || []);

  // Email Integrations
  const [emailIntegrations, setEmailIntegrations] = useState(chatbot.emailIntegrations || []);

  const [selectedFiles, setSelectedFiles] = useState(chatbot.selectedFiles || []);

  // Proactive Bubble Settings
  const [proactiveBubbleEnabled, setProactiveBubbleEnabled] = useState(chatbot.proactiveBubbleEnabled !== false);
  const [proactiveBubbleDelay, setProactiveBubbleDelay] = useState(chatbot.proactiveBubbleDelay || 5);
  const [proactiveBubbleMode, setProactiveBubbleMode] = useState(chatbot.proactiveBubbleMode || 'ai');
  const [proactiveBubbleDefaultMessage, setProactiveBubbleDefaultMessage] = useState(chatbot.proactiveBubbleDefaultMessage || 'Have a question? Ask me anything!');

  // Master Widget Toggle - allows disabling widget remotely without removing embed code
  const [widgetEnabled, setWidgetEnabled] = useState(chatbot.widgetEnabled !== false);

  // Use activeSubTab from props
  const currentSubTab = activeSubTab || 'Profile';

  // When the chatbot prop changes, update local state accordingly
  useEffect(() => {
    if (chatbot) {
      setOpenAIAPIKey(chatbot.openAIAPIKey || '');
      setAssistantName(chatbot.assistantName || '');
      setMessagePrompt(chatbot.messagePrompt || '');
      setIntroMessageText(chatbot.introMessageText || '');
      setCustomIntroMessage(chatbot.customIntroMessage || false);
      setDefaultLanguage(chatbot.defaultLanguage || 'en');
      setInternalMessagePrompt(chatbot.internalMessagePrompt || '');

      setBotTagline(chatbot.botTagline || '');
      setHumanHandoff(chatbot.humanHandoff || null);
      setOfficeHours(chatbot.officeHours || null);

      setHeaderColor(chatbot.headerColor || '');
      setChatBackgroundColor(chatbot.chatBackgroundColor || '');
      setBotMessageBoxColor(chatbot.botMessageBoxColor || '');
      setBotMessageTextColor(chatbot.botMessageTextColor || '#333333');
      setUserMessageBoxColor(chatbot.userMessageBoxColor || '');
      setUserMessageTextColor(chatbot.userMessageTextColor || '#ffffff');
      setHeaderStyle(chatbot.headerStyle || 'modern');
      setBotIconUrl(chatbot.botIconUrl || '');
      setBotIconOffsetX(chatbot.botIconOffsetX || 0);
      setBotIconOffsetY(chatbot.botIconOffsetY || 0);
      setExcludedUrls(chatbot.excludedUrls || []);

      setOpenAIVectorStoreID(chatbot.openAIVectorStoreID || '');
      setOpenAIAssistantID(chatbot.openAIAssistantID || '');
      setChatbotRolePrompt(chatbot.chatbotRolePrompt || '');
      setAiModelTier(chatbot.aiModelTier || 'standard');
      setSoundNotificationEnabled(chatbot.soundNotificationEnabled !== false);
      // Note: customAPIEndpoint, responseDelay, openChatOnLoad, chatOpenDelay remain unchanged

      // Toggle Button settings
      setToggleButtonSize(chatbot.toggleButtonSize || 'medium');
      setToggleButtonIcon(chatbot.toggleButtonIcon || '');
      setCustomToggleButtonIcon(chatbot.customToggleButtonIcon || '');
      setToggleButtonIconSize(chatbot.toggleButtonIconSize || '60');
      setToggleButtonBackgroundColor(chatbot.toggleButtonBackgroundColor || '#1a1a1a');
      setToggleButtonIconOffsetX(chatbot.toggleButtonIconOffsetX || 0);

      setToggleButtonIconOffsetY(chatbot.toggleButtonIconOffsetY || 0);
      setToggleButtonOutlineEnabled(chatbot.toggleButtonOutlineEnabled || false);
      setToggleButtonOutlineColor(chatbot.toggleButtonOutlineColor || '#ffffff');

      // Menu Items
      setMenuItems(chatbot.menuItems || []);

      // Email Integrations
      setEmailIntegrations(chatbot.emailIntegrations || []);

      // Master Widget Toggle
      setWidgetEnabled(chatbot.widgetEnabled !== false);
    }
  }, [chatbot]);

  // Determine if there are unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    // Only check if chatbot object exists and has properties
    if (!chatbot || Object.keys(chatbot).length === 0) return;

    // Helper to safely stringify and compare
    const normalize = (val) => {
      if (val === null || val === undefined) return '';
      if (typeof val === 'object') return JSON.stringify(val);
      return String(val);
    };

    // Compare current state to original chatbot props
    const hasChanges =
      normalize(assistantName) !== normalize(chatbot.assistantName) ||
      normalize(messagePrompt) !== normalize(chatbot.messagePrompt) ||
      normalize(introMessageText) !== normalize(chatbot.introMessageText) ||
      !!customIntroMessage !== !!chatbot.customIntroMessage ||
      normalize(defaultLanguage) !== normalize(chatbot.defaultLanguage) ||
      normalize(internalMessagePrompt) !== normalize(chatbot.internalMessagePrompt) ||
      normalize(botTagline) !== normalize(chatbot.botTagline) ||
      normalize(headerColor) !== normalize(chatbot.headerColor) ||
      normalize(chatBackgroundColor) !== normalize(chatbot.chatBackgroundColor) ||
      normalize(botMessageBoxColor) !== normalize(chatbot.botMessageBoxColor) ||
      normalize(botMessageTextColor) !== normalize(chatbot.botMessageTextColor) ||
      normalize(userMessageBoxColor) !== normalize(chatbot.userMessageBoxColor) ||
      normalize(userMessageTextColor) !== normalize(chatbot.userMessageTextColor) ||
      normalize(headerStyle) !== (chatbot.headerStyle || 'modern') ||
      normalize(botIconUrl) !== normalize(chatbot.botIconUrl) ||
      normalize(botIconOffsetX) !== (chatbot.botIconOffsetX ? String(chatbot.botIconOffsetX) : '0') ||
      normalize(botIconOffsetY) !== (chatbot.botIconOffsetY ? String(chatbot.botIconOffsetY) : '0') ||
      normalize(excludedUrls) !== normalize(chatbot.excludedUrls || []) ||
      normalize(openAIVectorStoreID) !== normalize(chatbot.openAIVectorStoreID) ||
      normalize(openAIAssistantID) !== normalize(chatbot.openAIAssistantID) ||
      normalize(chatbotRolePrompt) !== normalize(chatbot.chatbotRolePrompt) ||
      normalize(aiModelTier) !== (chatbot.aiModelTier || 'standard') ||
      !!soundNotificationEnabled !== (typeof chatbot.soundNotificationEnabled !== 'undefined' ? !!chatbot.soundNotificationEnabled : true) ||
      !!showPoweredBy !== (typeof chatbot.showPoweredBy !== 'undefined' ? !!chatbot.showPoweredBy : true) ||
      !!showLinkPreviews !== (typeof chatbot.showLinkPreviews !== 'undefined' ? !!chatbot.showLinkPreviews : true) ||
      normalize(announcement) !== normalize(chatbot.announcement) ||
      normalize(openAIAPIKey) !== normalize(chatbot.openAIAPIKey) ||
      normalize(wooCommerceApiKey) !== normalize(chatbot.wooCommerceApiKey) ||
      normalize(wooCommerceApiSecret) !== normalize(chatbot.wooCommerceApiSecret) ||
      normalize(wooCommerceStoreUrl) !== normalize(chatbot.wooCommerceStoreUrl) ||
      !!wooCommerceEnabled !== !!chatbot.wooCommerceEnabled ||
      !!whatsAppEnabled !== !!chatbot.whatsAppEnabled ||
      normalize(whatsAppPhoneNumber) !== normalize(chatbot.whatsAppPhoneNumber) ||
      normalize(whatsAppPhoneNumberId) !== normalize(chatbot.whatsAppPhoneNumberId) ||
      normalize(whatsAppBusinessAccountId) !== normalize(chatbot.whatsAppBusinessAccountId) ||
      normalize(whatsAppDisplayName) !== normalize(chatbot.whatsAppDisplayName) ||
      normalize(whatsAppAccessToken) !== normalize(chatbot.whatsAppAccessToken) ||
      normalize(humanHandoff) !== normalize(chatbot.humanHandoff) ||
      normalize(officeHours) !== normalize(chatbot.officeHours) ||
      normalize(toggleButtonSize) !== (chatbot.toggleButtonSize || 'medium') ||
      normalize(toggleButtonIcon) !== normalize(chatbot.toggleButtonIcon) ||
      normalize(customToggleButtonIcon) !== normalize(chatbot.customToggleButtonIcon) ||
      normalize(toggleButtonIconSize) !== (chatbot.toggleButtonIconSize ? String(chatbot.toggleButtonIconSize) : '60') ||
      normalize(toggleButtonBackgroundColor) !== (chatbot.toggleButtonBackgroundColor || '#1a1a1a') ||
      normalize(toggleButtonIconOffsetX) !== (chatbot.toggleButtonIconOffsetX ? String(chatbot.toggleButtonIconOffsetX) : '0') ||
      normalize(toggleButtonIconOffsetY) !== (chatbot.toggleButtonIconOffsetY ? String(chatbot.toggleButtonIconOffsetY) : '0') ||
      !!toggleButtonOutlineEnabled !== !!chatbot.toggleButtonOutlineEnabled ||
      normalize(toggleButtonOutlineColor) !== (chatbot.toggleButtonOutlineColor || '#ffffff') ||
      normalize(menuItems) !== normalize(chatbot.menuItems || []) ||
      normalize(emailIntegrations) !== normalize(chatbot.emailIntegrations || []) ||
      !!proactiveBubbleEnabled !== (typeof chatbot.proactiveBubbleEnabled !== 'undefined' ? !!chatbot.proactiveBubbleEnabled : true) ||
      normalize(proactiveBubbleDelay) !== (chatbot.proactiveBubbleDelay ? String(chatbot.proactiveBubbleDelay) : '5') ||
      normalize(proactiveBubbleMode) !== (chatbot.proactiveBubbleMode || 'ai') ||
      normalize(proactiveBubbleDefaultMessage) !== (chatbot.proactiveBubbleDefaultMessage || 'Have a question? Ask me anything!') ||
      !!widgetEnabled !== (typeof chatbot.widgetEnabled !== 'undefined' ? !!chatbot.widgetEnabled : true) ||
      normalize(chatbotPosition) !== (chatbot.chatbotPosition || 'right');

    setHasUnsavedChanges(hasChanges);
  }, [
    chatbot, assistantName, messagePrompt, introMessageText, customIntroMessage,
    defaultLanguage, internalMessagePrompt, botTagline, headerColor, chatBackgroundColor,
    botMessageBoxColor, botMessageTextColor, userMessageBoxColor, userMessageTextColor,
    headerStyle, botIconUrl, botIconOffsetX, botIconOffsetY, excludedUrls, openAIVectorStoreID, openAIAssistantID,
    chatbotRolePrompt, aiModelTier, soundNotificationEnabled, showPoweredBy, showLinkPreviews, announcement,
    openAIAPIKey, wooCommerceApiKey, wooCommerceApiSecret, wooCommerceStoreUrl,
    wooCommerceEnabled, whatsAppEnabled, whatsAppPhoneNumber, whatsAppPhoneNumberId,
    whatsAppBusinessAccountId, whatsAppDisplayName, whatsAppAccessToken, humanHandoff,
    officeHours, toggleButtonSize, toggleButtonIcon, customToggleButtonIcon,
    toggleButtonIconSize, toggleButtonBackgroundColor, toggleButtonIconOffsetX,
    toggleButtonIconOffsetY, toggleButtonOutlineEnabled, toggleButtonOutlineColor,
    menuItems, emailIntegrations, proactiveBubbleEnabled, proactiveBubbleDelay,
    proactiveBubbleMode, proactiveBubbleDefaultMessage, widgetEnabled, chatbotPosition
  ]);

  // Initialize announcement from chatbot config if it exists
  useEffect(() => {
    if (chatbot.announcement) {
      setAnnouncement(chatbot.announcement);
    }
  }, [chatbot, setAnnouncement]);

  // IMPLEMENT updateConfig:
  // This function takes an object of key/value pairs and updates the corresponding state.
  const updateConfig = useCallback((updates) => {
    if (typeof updates === 'object') {
      Object.entries(updates).forEach(([key, value]) => {
        switch (key) {
          case 'assistantName':
            setAssistantName(value);
            break;
          case 'messagePrompt':
            setMessagePrompt(value);
            break;
          case 'introMessageText':
            setIntroMessageText(value);
            break;
          case 'customIntroMessage':
            setCustomIntroMessage(value);
            break;
          case 'defaultLanguage':
            setDefaultLanguage(value);
            break;
          case 'internalMessagePrompt':
            setInternalMessagePrompt(value);
            break;
          case 'headerColor':
            setHeaderColor(value);
            break;
          case 'chatBackgroundColor':
            setChatBackgroundColor(value);
            break;
          case 'botMessageBoxColor':
            setBotMessageBoxColor(value);
            break;
          case 'botMessageTextColor':
            setBotMessageTextColor(value);
            break;
          case 'userMessageBoxColor':
            setUserMessageBoxColor(value);
            break;
          case 'userMessageTextColor':
            setUserMessageTextColor(value);
            break;
          case 'openAIVectorStoreID':
            setOpenAIVectorStoreID(value);
            break;
          case 'openAIAssistantID':
            setOpenAIAssistantID(value);
            break;
          case 'chatbotRolePrompt':
            setChatbotRolePrompt(value);
            break;
          case 'aiModelTier':
            setAiModelTier(value);
            break;
          case 'customAPIEndpoint':
            setCustomAPIEndpoint(value);
            break;
          case 'responseDelay':
            setResponseDelay(value);
            break;
          case 'openChatOnLoad':
            setOpenChatOnLoad(value);
            break;
          case 'chatOpenDelay':
            setChatOpenDelay(value);
            break;
          case 'soundNotificationEnabled':
            setSoundNotificationEnabled(value);
            break;
          case 'showPoweredBy':
            setShowPoweredBy(value);
            break;
          case 'showLinkPreviews':
            setShowLinkPreviews(value);
            break;
          case 'announcement':
            setAnnouncement(value);
            break;
          case 'openAIAPIKey':
            setOpenAIAPIKey(value);
            break;
          case 'wooCommerceApiKey':
            setWooCommerceApiKey(value);
            break;
          case 'wooCommerceApiSecret':
            setWooCommerceApiSecret(value);
            break;
          case 'wooCommerceStoreUrl':
            setWooCommerceStoreUrl(value);
            break;
          case 'wooCommerceEnabled':
            setWooCommerceEnabled(value);
            break;
          case 'whatsAppEnabled':
            setWhatsAppEnabled(value);
            break;
          case 'whatsAppPhoneNumber':
            setWhatsAppPhoneNumber(value);
            break;
          case 'whatsAppPhoneNumberId':
            setWhatsAppPhoneNumberId(value);
            break;
          case 'whatsAppBusinessAccountId':
            setWhatsAppBusinessAccountId(value);
            break;
          case 'whatsAppDisplayName':
            setWhatsAppDisplayName(value);
            break;
          case 'whatsAppAccessToken':
            setWhatsAppAccessToken(value);
            break;
          case 'humanHandoff':
            setHumanHandoff(value);
            break;
          case 'officeHours':
            setOfficeHours(value);
            break;

          case 'toggleButtonSize':
            setToggleButtonSize(value);
            break;
          case 'toggleButtonIcon':
            setToggleButtonIcon(value);
            break;
          case 'customToggleButtonIcon':
            setCustomToggleButtonIcon(value);
            break;
          case 'toggleButtonIconSize':
            setToggleButtonIconSize(value);
            break;
          case 'toggleButtonBackgroundColor':
            setToggleButtonBackgroundColor(value);
            break;
          case 'toggleButtonIconOffsetX':
            setToggleButtonIconOffsetX(value);
            break;
          case 'toggleButtonIconOffsetY':
            setToggleButtonIconOffsetY(value);
            break;
          case 'toggleButtonOutlineEnabled':
            setToggleButtonOutlineEnabled(value);
            break;
          case 'toggleButtonOutlineColor':
            setToggleButtonOutlineColor(value);
            break;
          case 'botTagline':
            setBotTagline(value);
            break;
          case 'headerStyle':
            setHeaderStyle(value);
            break;
          case 'botIconUrl':
            setBotIconUrl(value);
            break;
          case 'botIconOffsetX':
            setBotIconOffsetX(value);
            break;
          case 'botIconOffsetY':
            setBotIconOffsetY(value);
            break;
          case 'excludedUrls':
            setExcludedUrls(Array.isArray(value) ? value : []);
            break;
          case 'proactiveBubbleEnabled':
            setProactiveBubbleEnabled(value);
            break;
          case 'proactiveBubbleDelay':
            setProactiveBubbleDelay(value);
            break;
          case 'proactiveBubbleMode':
            setProactiveBubbleMode(value);
            break;
          case 'proactiveBubbleDefaultMessage':
            setProactiveBubbleDefaultMessage(value);
            break;
          case 'menuItems':
            setMenuItems(value);
            break;
          case 'emailIntegrations':
            setEmailIntegrations(value);
            break;
          case 'widgetEnabled':
            setWidgetEnabled(value);
            break;
          default:
            break;
        }
      });
    }
    // Note: useState setters are stable, so they don't actually need to be dependencies
    // but we include them to satisfy the react-hooks/exhaustive-deps rule
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const user = auth.currentUser;

  const handleSaveConfig = async (newConfig = {}) => {
    setIsSaving(true);
    setSaveSuccess(false);
    try {
      // Get the Firebase token for authentication
      const token = user ? await user.getIdToken() : null;

      const headers = {
        'Content-Type': 'application/json',
      };

      // Add Authorization header if token is available
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const configToSave = {
        ...newConfig,
        // Explicitly include all state variables to ensure they are saved
        // regardless of which component triggers the save
        assistantName: newConfig.assistantName ?? assistantName,
        messagePrompt: newConfig.messagePrompt ?? messagePrompt,
        introMessageText: newConfig.introMessageText ?? introMessageText,
        customIntroMessage: newConfig.customIntroMessage ?? customIntroMessage,
        defaultLanguage: newConfig.defaultLanguage ?? defaultLanguage,
        internalMessagePrompt: newConfig.internalMessagePrompt ?? internalMessagePrompt,
        botTagline: newConfig.botTagline ?? botTagline,
        headerColor: newConfig.headerColor ?? headerColor,
        chatBackgroundColor: newConfig.chatBackgroundColor ?? chatBackgroundColor,
        botMessageBoxColor: newConfig.botMessageBoxColor ?? botMessageBoxColor,
        botMessageTextColor: newConfig.botMessageTextColor ?? botMessageTextColor,
        userMessageBoxColor: newConfig.userMessageBoxColor ?? userMessageBoxColor,
        userMessageTextColor: newConfig.userMessageTextColor ?? userMessageTextColor,
        headerStyle: newConfig.headerStyle ?? headerStyle,
        botIconUrl: newConfig.botIconUrl ?? botIconUrl,
        botIconOffsetX: newConfig.botIconOffsetX ?? botIconOffsetX,
        botIconOffsetY: newConfig.botIconOffsetY ?? botIconOffsetY,
        excludedUrls: newConfig.excludedUrls ?? excludedUrls,
        openAIVectorStoreID: newConfig.openAIVectorStoreID ?? openAIVectorStoreID,
        openAIAssistantID: newConfig.openAIAssistantID ?? openAIAssistantID,
        chatbotRolePrompt: newConfig.chatbotRolePrompt ?? chatbotRolePrompt,
        aiModelTier: newConfig.aiModelTier ?? aiModelTier,
        customAPIEndpoint: newConfig.customAPIEndpoint ?? customAPIEndpoint,
        responseDelay: newConfig.responseDelay ?? responseDelay,
        openChatOnLoad: newConfig.openChatOnLoad ?? openChatOnLoad,
        chatOpenDelay: newConfig.chatOpenDelay ?? chatOpenDelay,
        soundNotificationEnabled: newConfig.soundNotificationEnabled ?? soundNotificationEnabled,
        showPoweredBy: newConfig.showPoweredBy ?? showPoweredBy,
        showLinkPreviews: newConfig.showLinkPreviews ?? showLinkPreviews,
        announcement: newConfig.announcement ?? announcement,
        openAIAPIKey: newConfig.openAIAPIKey ?? openAIAPIKey,

        // WooCommerce
        wooCommerceApiKey: newConfig.wooCommerceApiKey ?? wooCommerceApiKey,
        wooCommerceApiSecret: newConfig.wooCommerceApiSecret ?? wooCommerceApiSecret,
        wooCommerceStoreUrl: newConfig.wooCommerceStoreUrl ?? wooCommerceStoreUrl,
        wooCommerceEnabled: newConfig.wooCommerceEnabled ?? wooCommerceEnabled,
        orderTrackingEnabled: newConfig.orderTrackingEnabled ?? (wooCommerceEnabled), // usually tied

        // WhatsApp
        whatsAppEnabled: newConfig.whatsAppEnabled ?? whatsAppEnabled,
        whatsAppPhoneNumber: newConfig.whatsAppPhoneNumber ?? whatsAppPhoneNumber,
        whatsAppPhoneNumberId: newConfig.whatsAppPhoneNumberId ?? whatsAppPhoneNumberId,
        whatsAppBusinessAccountId: newConfig.whatsAppBusinessAccountId ?? whatsAppBusinessAccountId,
        whatsAppDisplayName: newConfig.whatsAppDisplayName ?? whatsAppDisplayName,
        whatsAppAccessToken: newConfig.whatsAppAccessToken ?? whatsAppAccessToken,

        // Toggle Button
        toggleButtonSize: newConfig.toggleButtonSize ?? toggleButtonSize,
        toggleButtonIcon: newConfig.toggleButtonIcon ?? toggleButtonIcon,
        customToggleButtonIcon: newConfig.customToggleButtonIcon ?? customToggleButtonIcon,
        toggleButtonIconSize: newConfig.toggleButtonIconSize ?? toggleButtonIconSize,
        toggleButtonBackgroundColor: newConfig.toggleButtonBackgroundColor ?? toggleButtonBackgroundColor,
        toggleButtonIconOffsetX: newConfig.toggleButtonIconOffsetX ?? toggleButtonIconOffsetX,
        toggleButtonIconOffsetY: newConfig.toggleButtonIconOffsetY ?? toggleButtonIconOffsetY,
        // Ensure outline settings are saved
        toggleButtonOutlineEnabled: newConfig.toggleButtonOutlineEnabled ?? toggleButtonOutlineEnabled,
        toggleButtonOutlineColor: newConfig.toggleButtonOutlineColor ?? toggleButtonOutlineColor,

        // Proactive Bubble
        proactiveBubbleEnabled: newConfig.proactiveBubbleEnabled ?? proactiveBubbleEnabled,
        proactiveBubbleDelay: newConfig.proactiveBubbleDelay ?? proactiveBubbleDelay,
        proactiveBubbleMode: newConfig.proactiveBubbleMode ?? proactiveBubbleMode,
        proactiveBubbleDefaultMessage: newConfig.proactiveBubbleDefaultMessage ?? proactiveBubbleDefaultMessage,

        // Master Widget Toggle
        widgetEnabled: newConfig.widgetEnabled ?? widgetEnabled,

        // Chat Menu
        menuItems: newConfig.menuItems ?? menuItems,

        // Email Integrations
        emailIntegrations: newConfig.emailIntegrations ?? emailIntegrations,

        // Human Handoff & Office Hours
        humanHandoff: newConfig.humanHandoff ?? humanHandoff,
        officeHours: newConfig.officeHours ?? officeHours,

        selectedFiles
      };

      const response = await fetch('/api/chatbot', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          chatbotId: localStorage.getItem('chatbotId'),
          config: configToSave,
          userId: user?.uid
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save configuration');
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      logger.error('Error saving configuration:', error);
    } finally {
      setIsSaving(false);
    }
  };


  // Fetch selectedFiles from chatbot's assigned files when chatbot loads
  useEffect(() => {
    const fetchChatbotFiles = async () => {
      const chatbotId = localStorage.getItem('chatbotId');
      if (chatbotId) {
        try {
          const response = await axios.get(`/api/chatbot/${chatbotId}/files`);
          const chatbotFiles = response.data.files || [];
          const fileIds = chatbotFiles.map(file => file.id.toString());
          setSelectedFiles(fileIds);
        } catch (error) {
          logger.error('Error fetching chatbot files:', error);
        }
      }
    };

    if (chatbot && Object.keys(chatbot).length > 0) {
      fetchChatbotFiles();
    }
  }, [chatbot]);

  return (
    <div className="chatbot-layout relative">
      {/* Global Save Changes Float */}
      {hasUnsavedChanges && !['Files', 'Integration', 'Publish'].includes(activeSubTab) && (
        <div className="absolute bottom-6 left-[calc(50%-235px)] -translate-x-1/2 z-[100] flex items-center gap-3 bg-white/95 backdrop-blur-md pl-4 pr-1.5 py-1.5 rounded-full shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-slate-200/60 animate-in slide-in-from-bottom-5">
          <span className="text-xs font-semibold text-slate-500 mr-1 hidden sm:block">Update configuration</span>
          <button
            onClick={() => handleSaveConfig({})}
            disabled={isSaving || saveSuccess}
            className="px-5 py-2.5 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-800 text-white rounded-full text-sm font-semibold transition-all flex items-center gap-2 transform active:scale-95"
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-slate-300 border-t-white rounded-full animate-spin" />
                Saving...
              </>
            ) : saveSuccess ? (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                Saved
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      )}

      {/* Edit Section */}
      <div className="chatbot-edit-section relative">

        {currentSubTab === 'Identity' && (
          <Identity
            // Profile Props
            assistantName={assistantName}
            setAssistantName={(value) => updateConfig({ assistantName: value })}
            botTagline={botTagline}
            setBotTagline={(value) => updateConfig({ botTagline: value })}

            // Visual Props
            botIconUrl={botIconUrl}
            setBotIconUrl={(value) => updateConfig({ botIconUrl: value })}
            botIconOffsetX={botIconOffsetX}
            botIconOffsetY={botIconOffsetY}

            // Behavior Props
            defaultLanguage={defaultLanguage}
            setDefaultLanguage={(value) => updateConfig({ defaultLanguage: value })}
            messagePrompt={messagePrompt} // Keep for future use or remove if unused, but good to have
            setMessagePrompt={(value) => updateConfig({ messagePrompt: value })}

            // Intro Props
            introMessageText={introMessageText}
            setIntroMessageText={(value) => updateConfig({ introMessageText: value })}
            customIntroMessage={customIntroMessage}
            setCustomIntroMessage={(value) => updateConfig({ customIntroMessage: value })}

            // Config Handlers
            updateConfig={updateConfig}
          />
        )}
        {currentSubTab === 'Profile' && (
          <BasicSettings
            isEditMode={!!chatbot}
            assistantName={assistantName}
            setAssistantName={(value) => updateConfig({ assistantName: value })}
            messagePrompt={messagePrompt}
            setMessagePrompt={(value) => updateConfig({ messagePrompt: value })}
            introMessageText={introMessageText}
            setIntroMessageText={(value) => updateConfig({ introMessageText: value })}
            customIntroMessage={customIntroMessage}
            setCustomIntroMessage={(value) => updateConfig({ customIntroMessage: value })}
            defaultLanguage={defaultLanguage}
            setDefaultLanguage={(value) => updateConfig({ defaultLanguage: value })}
            setInternalMessagePrompt={(value) => updateConfig({ internalMessagePrompt: value })}
            chatbotId={localStorage.getItem('chatbotId')}
            chatbotRolePrompt={chatbotRolePrompt}
            setChatbotRolePrompt={(value) => updateConfig({ chatbotRolePrompt: value })}
            botTagline={botTagline}
            setBotTagline={(value) => updateConfig({ botTagline: value })}
          />
        )}
        {currentSubTab === 'Theme' && (
          <StyleSettings
            isEditMode={!!chatbot}
            headerColor={headerColor}
            setHeaderColor={(value) => updateConfig({ headerColor: value })}
            chatBackgroundColor={chatBackgroundColor}
            setChatBackgroundColor={(value) => updateConfig({ chatBackgroundColor: value })}
            botMessageBoxColor={botMessageBoxColor}
            setBotMessageBoxColor={(value) => updateConfig({ botMessageBoxColor: value })}
            botMessageTextColor={botMessageTextColor}
            setBotMessageTextColor={(value) => updateConfig({ botMessageTextColor: value })}
            userMessageBoxColor={userMessageBoxColor}
            setUserMessageBoxColor={(value) => updateConfig({ userMessageBoxColor: value })}
            userMessageTextColor={userMessageTextColor}
            setUserMessageTextColor={(value) => updateConfig({ userMessageTextColor: value })}
            headerStyle={headerStyle}
            setHeaderStyle={(value) => updateConfig({ headerStyle: value })}
            botIconUrl={botIconUrl}
            setBotIconUrl={(value) => updateConfig({ botIconUrl: value })}
            updateConfig={updateConfig}
          />
        )}
        {currentSubTab === 'Toggle Button' && (
          <ToggleButton
            isEditMode={!!chatbot}
            toggleButtonSize={toggleButtonSize}
            setToggleButtonSize={(value) => updateConfig({ toggleButtonSize: value })}
            toggleButtonIcon={toggleButtonIcon}
            setToggleButtonIcon={(value) => updateConfig({ toggleButtonIcon: value })}
            customToggleButtonIcon={customToggleButtonIcon}
            setCustomToggleButtonIcon={(value) => updateConfig({ customToggleButtonIcon: value })}
            toggleButtonIconSize={toggleButtonIconSize}
            setToggleButtonIconSize={(value) => updateConfig({ toggleButtonIconSize: value })}
            toggleButtonBackgroundColor={toggleButtonBackgroundColor}
            setToggleButtonBackgroundColor={(value) => updateConfig({ toggleButtonBackgroundColor: value })}
            toggleButtonIconOffsetX={toggleButtonIconOffsetX}
            setToggleButtonIconOffsetX={(value) => updateConfig({ toggleButtonIconOffsetX: value })}
            toggleButtonIconOffsetY={toggleButtonIconOffsetY}
            setToggleButtonIconOffsetY={(value) => updateConfig({ toggleButtonIconOffsetY: value })}
            toggleButtonOutlineEnabled={toggleButtonOutlineEnabled}
            setToggleButtonOutlineEnabled={(value) => updateConfig({ toggleButtonOutlineEnabled: value })}
            toggleButtonOutlineColor={toggleButtonOutlineColor}
            setToggleButtonOutlineColor={(value) => updateConfig({ toggleButtonOutlineColor: value })}
            updateConfig={updateConfig}
          />
        )}
        {currentSubTab === 'Advance' && (
          <AdvanceSettings
            isEditMode={!!chatbot}
            openAIVectorStoreID={openAIVectorStoreID}
            setOpenAIVectorStoreID={(value) => updateConfig({ openAIVectorStoreID: value })}
            openAIAssistantID={openAIAssistantID}
            setOpenAIAssistantID={(value) => updateConfig({ openAIAssistantID: value })}
            chatbotId={chatbot.chatbotId}
            openAIAPIKey={openAIAPIKey}
            setOpenAIAPIKey={(value) => updateConfig({ openAIAPIKey: value })}
            chatbotRolePrompt={chatbotRolePrompt}
            setChatbotRolePrompt={(value) => updateConfig({ chatbotRolePrompt: value })}
            aiModelTier={aiModelTier}
            setAiModelTier={(value) => updateConfig({ aiModelTier: value })}
            customAPIEndpoint={customAPIEndpoint}
            setCustomAPIEndpoint={(value) => updateConfig({ customAPIEndpoint: value })}
            responseDelay={responseDelay}
            setResponseDelay={(value) => updateConfig({ responseDelay: value })}
            openChatOnLoad={openChatOnLoad}
            setOpenChatOnLoad={(value) => updateConfig({ openChatOnLoad: value })}
            chatOpenDelay={chatOpenDelay}
            setChatOpenDelay={(value) => updateConfig({ chatOpenDelay: value })}
            soundNotificationEnabled={soundNotificationEnabled}
            setSoundNotificationEnabled={(value) => updateConfig({ soundNotificationEnabled: value })}
            updateConfig={updateConfig}
            showPoweredBy={showPoweredBy}
            setShowPoweredBy={(value) => updateConfig({ showPoweredBy: value })}
            showLinkPreviews={showLinkPreviews}
            setShowLinkPreviews={(value) => updateConfig({ showLinkPreviews: value })}
            widgetPosition={chatbotPosition}
            setWidgetPosition={setChatbotPosition}
            announcement={announcement}
            setAnnouncement={(value) => updateConfig({ announcement: value })}
            botTagline={botTagline}
            setBotTagline={(value) => updateConfig({ botTagline: value })}
            excludedUrls={excludedUrls}
            setExcludedUrls={(value) => updateConfig({ excludedUrls: value })}
            officeHours={officeHours}
            setOfficeHours={(value) => updateConfig({ officeHours: value })}
          />
        )}
        {currentSubTab === 'HumanHandoff' && (
          <HumanHandoff
            isEditMode={!!chatbot}
            humanHandoff={humanHandoff}
            setHumanHandoff={(value) => updateConfig({ humanHandoff: value })}
            updateConfig={updateConfig}
            emailIntegrations={emailIntegrations}
            setActiveSubTab={setActiveSubTab} // Pass this so we can redirect to Integration tab
          />
        )}
        {currentSubTab === 'ProactiveBubble' && (
          <ProactiveBubbleSettings
            isEditMode={!!chatbot}
            updateConfig={updateConfig}
            proactiveBubbleEnabled={proactiveBubbleEnabled}
            setProactiveBubbleEnabled={(value) => updateConfig({ proactiveBubbleEnabled: value })}
            proactiveBubbleDelay={proactiveBubbleDelay}
            setProactiveBubbleDelay={(value) => updateConfig({ proactiveBubbleDelay: value })}
            proactiveBubbleMode={proactiveBubbleMode}
            setProactiveBubbleMode={(value) => updateConfig({ proactiveBubbleMode: value })}
            proactiveBubbleDefaultMessage={proactiveBubbleDefaultMessage}
            setProactiveBubbleDefaultMessage={(value) => updateConfig({ proactiveBubbleDefaultMessage: value })}
          />
        )}
        {currentSubTab === 'Integration' && (
          <Integration
            isEditMode={!!chatbot}
            updateConfig={updateConfig}
            chatbotId={localStorage.getItem('chatbotId')}
            wooCommerceApiKey={wooCommerceApiKey}
            setWooCommerceApiKey={(value) => updateConfig({ wooCommerceApiKey: value })}
            wooCommerceApiSecret={wooCommerceApiSecret}
            setWooCommerceApiSecret={(value) => updateConfig({ wooCommerceApiSecret: value })}
            wooCommerceStoreUrl={wooCommerceStoreUrl}
            setWooCommerceStoreUrl={(value) => updateConfig({ wooCommerceStoreUrl: value })}
            wooCommerceEnabled={wooCommerceEnabled}
            setWooCommerceEnabled={(value) => updateConfig({ wooCommerceEnabled: value })}
            orderTrackingEnabled={wooCommerceEnabled}
            setOrderTrackingEnabled={(value) => updateConfig({ orderTrackingEnabled: value })}
            // WhatsApp Integration
            whatsAppEnabled={whatsAppEnabled}
            setWhatsAppEnabled={(value) => updateConfig({ whatsAppEnabled: value })}
            whatsAppPhoneNumber={whatsAppPhoneNumber}
            setWhatsAppPhoneNumber={(value) => updateConfig({ whatsAppPhoneNumber: value })}
            whatsAppPhoneNumberId={whatsAppPhoneNumberId}
            setWhatsAppPhoneNumberId={(value) => updateConfig({ whatsAppPhoneNumberId: value })}
            whatsAppBusinessAccountId={whatsAppBusinessAccountId}
            setWhatsAppBusinessAccountId={(value) => updateConfig({ whatsAppBusinessAccountId: value })}
            whatsAppDisplayName={whatsAppDisplayName}
            setWhatsAppDisplayName={(value) => updateConfig({ whatsAppDisplayName: value })}
            whatsAppAccessToken={whatsAppAccessToken}
            setWhatsAppAccessToken={(value) => updateConfig({ whatsAppAccessToken: value })}
            // Email Integration
            emailIntegrations={emailIntegrations}
            setEmailIntegrations={(value) => updateConfig({ emailIntegrations: value })}
            saveConfig={handleSaveConfig}
          />
        )}
        {currentSubTab === 'Chat Menu' && (
          <ChatMenu
            menuItems={menuItems}
            setMenuItems={(value) => updateConfig({ menuItems: value })}
            updateConfig={updateConfig}
            saveConfig={handleSaveConfig}
            chatbotId={localStorage.getItem('chatbotId')}
          />
        )}
        {currentSubTab === 'Files' && (
          <Files
            uploadedFiles={[]}
            handleFileUpload={() => { }}
            selectedChatbotId={localStorage.getItem('chatbotId')}
          />
        )}
        {currentSubTab === 'Forms' && (
          <Forms
            selectedChatbotId={localStorage.getItem('chatbotId')}
            onFormsChange={setForms}
          />
        )}
        {currentSubTab === 'Publish' && (
          <Publish
            assistantName={assistantName}
            messagePrompt={messagePrompt}
            introMessageText={introMessageText}
            headerColor={headerColor}
            chatBackgroundColor={chatBackgroundColor}
            botMessageBoxColor={botMessageBoxColor}
            botMessageTextColor={botMessageTextColor}
            userMessageBoxColor={userMessageBoxColor}
            userMessageTextColor={userMessageTextColor}
            openAIVectorStoreID={openAIVectorStoreID}
            openAIAssistantID={openAIAssistantID}
            openAIAPIKey={openAIAPIKey}
            chatbotRolePrompt={chatbotRolePrompt}
            aiModelTier={aiModelTier}
            responseDelay={responseDelay}
            openChatOnLoad={openChatOnLoad}
            chatOpenDelay={chatOpenDelay}
            internalMessagePrompt={internalMessagePrompt}
            isPublished={isPublished}
            showPoweredBy={showPoweredBy}
            showLinkPreviews={showLinkPreviews}
            widgetPosition={chatbotPosition}
            announcement={announcement}
            // WooCommerce configuration
            wooCommerceApiKey={wooCommerceApiKey}
            wooCommerceApiSecret={wooCommerceApiSecret}
            wooCommerceStoreUrl={wooCommerceStoreUrl}
            wooCommerceEnabled={wooCommerceEnabled}
            orderTrackingEnabled={wooCommerceEnabled}
            selectedFiles={selectedFiles}
            toggleButtonSize={toggleButtonSize}

            toggleButtonIcon={toggleButtonIcon}
            customToggleButtonIcon={customToggleButtonIcon}
            toggleButtonIconSize={toggleButtonIconSize}
            toggleButtonBackgroundColor={toggleButtonBackgroundColor}
            toggleButtonIconOffsetX={toggleButtonIconOffsetX}
            toggleButtonIconOffsetY={toggleButtonIconOffsetY}
            toggleButtonOutlineEnabled={toggleButtonOutlineEnabled}
            toggleButtonOutlineColor={toggleButtonOutlineColor}
            botTagline={botTagline}
            headerStyle={headerStyle}
            botIconUrl={botIconUrl}
            botIconOffsetX={botIconOffsetX}
            botIconOffsetY={botIconOffsetY}
            excludedUrls={excludedUrls}
            // Proactive Bubble settings
            proactiveBubbleEnabled={proactiveBubbleEnabled}
            proactiveBubbleDelay={proactiveBubbleDelay}
            proactiveBubbleMode={proactiveBubbleMode}
            proactiveBubbleDefaultMessage={proactiveBubbleDefaultMessage}
            // Master Widget Toggle
            widgetEnabled={widgetEnabled}
            setWidgetEnabled={(value) => updateConfig({ widgetEnabled: value })}
            // Sound Notification
            soundNotificationEnabled={soundNotificationEnabled}
            // Human Handoff
            humanHandoff={humanHandoff}
            officeHours={officeHours}
            saveConfig={handleSaveConfig}
          />
        )}
      </div>
      {/* Preview Section */}
      {!['Files', 'Integration', 'Publish'].includes(activeSubTab) && (
        <div className="chatbot-preview-section">
          <EmbedChatbot
            key={
              ['Chat Menu'].includes(activeSubTab) ? 'menu' :
                ['Forms'].includes(activeSubTab) ? 'forms' :
                  'standard'
            }
            isPreview={true}
            previewConfig={{
              assistantName,
              messagePrompt,
              introMessageText,
              headerColor,
              chatBackgroundColor,
              botMessageBoxColor,
              botMessageTextColor,
              userMessageBoxColor,
              userMessageTextColor,
              headerStyle,
              openAIVectorStoreID,
              openAIAssistantID,
              openAIAPIKey,
              chatbotRolePrompt,
              responseDelay,
              openChatOnLoad,
              chatOpenDelay,
              soundNotificationEnabled,
              showPoweredBy,
              showLinkPreviews,
              chatbotPosition,
              announcement,
              // WooCommerce Integration settings
              wooCommerceApiKey,
              wooCommerceApiSecret,
              wooCommerceStoreUrl,
              wooCommerceEnabled,
              orderTrackingEnabled: wooCommerceEnabled,

              // Add the current chatbotId to the preview config
              chatbotId: localStorage.getItem('chatbotId'),
              // Add the selected files to the preview config
              selectedFiles,
              toggleButtonSize,
              toggleButtonIcon,
              customToggleButtonIcon,
              toggleButtonIconSize,
              toggleButtonBackgroundColor,
              toggleButtonIconOffsetX,
              toggleButtonIconOffsetY,
              toggleButtonOutlineEnabled,
              toggleButtonOutlineColor,
              botTagline,
              botIconUrl,
              botIconOffsetX,
              botIconOffsetY,
              humanHandoff,
              officeHours,
              // Exclusions are for runtime loader; preview always shows the widget
              // Add menu items from state for preview
              menuItems: menuItems,

              // Pass live forms for preview
              forms: forms,

              // Preview State Overrides
              forceChatClosed: activeSubTab === 'Toggle Button',
              forceChatOpen: ['Identity', 'Theme', 'Chat Menu', 'Forms'].includes(activeSubTab),
              forceMenuOpen: activeSubTab === 'Chat Menu',
              forceShowFirstForm: activeSubTab === 'Forms',
              forceInputDisabled: (activeSubTab === 'Chat Menu' || activeSubTab === 'Forms'),
              // Hide toggle button for Menu and Forms to show expanded view by default
              hideToggleButton: (activeSubTab === 'Chat Menu' || activeSubTab === 'Forms'),
            }}
          />
        </div>
      )}
    </div>
  );
}

ChatbotBuilder.propTypes = {
  chatbot: PropTypes.shape({
    showPoweredBy: PropTypes.bool,
    showLinkPreviews: PropTypes.bool,
    chatbotPosition: PropTypes.string,
    assistantName: PropTypes.string,
    messagePrompt: PropTypes.string,
    introMessageText: PropTypes.string,
    customIntroMessage: PropTypes.bool,
    defaultLanguage: PropTypes.string,
    internalMessagePrompt: PropTypes.string,
    headerColor: PropTypes.string,
    chatBackgroundColor: PropTypes.string,
    botMessageBoxColor: PropTypes.string,
    botMessageTextColor: PropTypes.string,
    userMessageBoxColor: PropTypes.string,
    userMessageTextColor: PropTypes.string,
    openAIVectorStoreID: PropTypes.string,
    openAIAssistantID: PropTypes.string,
    chatbotRolePrompt: PropTypes.string,
    customAPIEndpoint: PropTypes.string,
    responseDelay: PropTypes.number,
    openChatOnLoad: PropTypes.bool,
    chatOpenDelay: PropTypes.number,
    // WooCommerce configuration
    wooCommerceApiKey: PropTypes.string,
    wooCommerceApiSecret: PropTypes.string,
    wooCommerceStoreUrl: PropTypes.string,
    wooCommerceEnabled: PropTypes.bool,
    orderTrackingEnabled: PropTypes.bool,
    selectedFiles: PropTypes.arrayOf(PropTypes.string),

    toggleButtonSize: PropTypes.string,
    toggleButtonIcon: PropTypes.string,
    customToggleButtonIcon: PropTypes.string,
    toggleButtonIconSize: PropTypes.string,
    toggleButtonBackgroundColor: PropTypes.string,
    toggleButtonOutlineEnabled: PropTypes.bool,
    toggleButtonOutlineColor: PropTypes.string,
    botTagline: PropTypes.string,
    headerStyle: PropTypes.oneOf(['compact', 'modern']),
    excludedUrls: PropTypes.arrayOf(PropTypes.string),
  }),
  onClose: PropTypes.func.isRequired,
  isPublished: PropTypes.bool.isRequired,
  announcement: PropTypes.shape({
    text: PropTypes.string.isRequired,
    enabled: PropTypes.bool.isRequired,
    style: PropTypes.oneOf(['info', 'warning', 'success']).isRequired,
  }).isRequired,
  setAnnouncement: PropTypes.func.isRequired,
};

export default ChatbotBuilder;
